export interface ActivationData {
  expireDate: string;
  maxUser: number;
  plan: string;
  isAppointmentEnabled: boolean;
  isFacialIdentificationEnabled: boolean;
  isSelectCompanyEnabled: boolean;
  isAppointmentHistoryEnabled: boolean;
  contractLanguages: string[];
  timezone: string;
  deviceName: string;
}

/** GET /activation/check レスポンス */
export interface ActivationCheckResponse {
  activation: {
    maxUser: number | null;
    expireDate: string; // YYYY-MM-DD
    timezone: string;
    contractLanguages: string[];
  };
  phoneBookId: number | null;
  phoneBookStartDateTime: string | null; // ISO date string
  appSettingUpdateDateTime: string | null; // ISO date string
}

export interface PhoneBooksData {
  phoneBookId: number;
  plan: string;
  isSelectCompanyEnabled: boolean;
  isAppointmentEnabled: boolean;
  isFacialIdentificationEnabled: boolean;
  isAppointmentHistoryEnabled: boolean;
  responseDataUrl: string;
}

export enum NotificationType {
  Call = "CALL",
  Chat = "CHAT",
  ChatAndCall = "CHAT_CALL",
}

export enum CallType {
  Phone = "PHONE",
  FaceTime = "FACETIME",
}

export enum ButtonType {
  GROUP = "GROUP",
  USER = "USER",
  RECRUIT = "RECRUIT",
  OTHER = "OTHER",
  SECRETARY = "SECRETARY",
  DELIVERY = "DELIVERY",
  TAXI = "TAXI",
  QR = "QR",
  RECEPTIONCODE = "RECEPTIONCODE",
  FACERECOGNITION = "FACERECOGNITION",
}

export enum PlanType {
  CALL = "CALL",
  CHAT = "CHAT",
  CHAT_CALL = "CHAT_CALL",
}
export enum ScreenType {
  MENU_RECEPTION = "Q",
  RECEPTION_CODE_INPUT = "N",
  VISITOR_INFO_INPUT = "P",
  DELIVERY_CONFIRMATION = "B",
  QR_SCAN = "R",
  DEPARTMENT_LIST = "J",
  EMPLOYEE_LIST = "K",
}

export interface NameItem {
  languageCode: string;
  name: string;
}

export interface PositionItem {
  languageCode: string;
  position: string;
}

export interface GroupEntity {
  groupId: number;
  displayId: number;
  names: NameItem[];
  notificationType: NotificationType;
  callType: CallType | null;
  extensionNumber: string | null;
  faceTimeAddress: string | null;
  parentGroupId: number | null;
  isRecept: boolean;
  order: number;
}

export interface UserEntity {
  groupUserId: number;
  groupId: number;
  names: NameItem[];
  kanaName: string | null;
  positions: PositionItem[];
  notificationType: NotificationType;
  callType: CallType | null;
  extensionNumber: string;
  faceTimeAddress: string;
  enable: boolean;
}

export interface PhoneBooksEntity {
  phoneBookId: number;
  plan: string;
  isSelectCompanyEnabled: boolean;
  isAppointmentEnabled: boolean;
  isFacialIdentificationEnabled: boolean;
  isAppointmentHistoryEnabled: boolean;
  groupList: GroupEntity[];
  userList: UserEntity[];
}

export interface PhoneBookAutoUpdateEntity {
  updateDate: string | null;
  lastUpdateDate: string | null;
}

export interface AppSettingAutoUpdateObject {
  updateDate: string | null;
  lastUpdateDate: string | null;
}

export interface SettingCommonEntity {
  id: number;
  settingLanguageCode: string;
  isDisplayLanguageButton: boolean;
  receptionLanguageCode: string;
  enableAutoSleep: boolean;
  openingHours: string;
  closingHours: string;
}

export interface StandbyEntity {
  id: number;
  standbyImageType: number;
  defaultStandbyImageType: number;
  standbyImageSwitchInterval: number;
  returnBackgroundTime: number;
  firstMessageText: string;
  secondMessageText: string;
  thirdMessageText: string;
  appTheme: number;
  standbyTextColor: number;
  animationType: number;
  flashTime: number;
  scrollTime: number;
  firstMessageScrollEnable: boolean;
  secondMessageScrollEnable: boolean;
  thirdMessageScrollEnable: boolean;
  isDisplayFingerIcon: boolean;
}

export interface CallInfoEntity {
  id: number;
  callLibrary: number;
  name: string;
  password: string;
  phoneNumber: string;
  serverAddress: string;
  serverPort: number;
  clientPort: number;
  displayName: string;
  callDisconnectTime: number;
  audioSessionMode: number;
  audioUnitSubType: number;
  samplingRate: number;
  buffer: number;
  switchBoardGain: number;
  expiresValue: number;
  secondsRetryAfterFail: number;
  ptimeMs: number;
  turnServerUrl?: string;
  turnUsername?: string;
  turnPassword?: string;
}

export interface ReceptionCommonWordEntity {
  id: number;
  items: Array<{
    languageCode: string;
    screenWords: ScreenWords;
  }>;
}

export interface MenuWordEntity {
  id: number;
  items: Array<{
    languageCode: string;
    menuWords: MenuWords;
  }>;
}

export interface DeliveryWordEntity {
  id: number;
  items: Array<{
    languageCode: string;
    deliveryWords: DeliveryWords;
  }>;
}

export interface SettingMenuButtonEntity {
  id: number;
  buttonOrder: {
    isDisplayGroupButton: boolean;
    isDisplayUserButton: boolean;
    isDisplayRecruitButton: boolean;
    isDisplayOtherButton: boolean;
    isDisplayDeliveryButton: boolean;
    isDisplaySecretaryButton: boolean;
    isDisplayTaxiButton: boolean;
    isDisplayQrButton: boolean;
    isDisplayReceptionCodeButton: boolean;
    isDisplayFaceRecognitionButton: boolean;
    isDisplayConversationReceptionButton: boolean;
    groupButtonIndex: number;
    userButtonIndex: number;
    recruitButtonIndex: number;
    deliveryButtonIndex: number;
    otherButtonIndex: number;
    secretaryButtonIndex?: number;
    taxiButtonIndex?: number;
    qrButtonIndex?: number;
    receptionCodeButtonIndex?: number;
    faceRecognitionButtonIndex?: number;
    conversationReceptionButtonIndex?: number;
  };
  isDisplayMenu: boolean;
  isDisplayAppointmentExists: boolean;
  isDisplayVisitorInfoGroup: boolean;
  isDisplayVisitorInfoUser: boolean;
  isDisplayVisitorInfoRecruit: boolean;
  isDisplayVisitorInfoOther: boolean;
  isDisplayVisitorInfoSecretary: boolean;
  deliveryTransitionType: number;
  deliveryMapImagePath: string | null;
  isDisplayInputExtensionNumber: boolean;
  recruitNotificationType: string;
  recruitExtensionNumber: string;
  recruitCallType: string;
  recruitFaceTimeAddress: string;
  deliveryNotificationType: string;
  deliveryExtensionNumber: string;
  deliveryCallType: string;
  deliveryFaceTimeAddress: string;
  otherNotificationType: string;
  otherExtensionNumber: string;
  otherCallType: string;
  otherFaceTimeAddress: string;
  taxiNotificationType: string;
  taxiExtensionNumber: string;
  taxiCallType: string;
  taxiFaceTimeAddress: string;
  secretaryNotificationType: string;
  secretaryExtensionNumber: string;
  secretaryCallType: string;
  secretaryFaceTimeAddress: string;
}

export interface SettingBasicEntity {
  id: number;
  isDisplayLogo: boolean;
  logoImage: string | null;
  isDisplayRequirement: boolean;
  isRequiredCompanyName: boolean;
  isRequiredName: boolean;
  isRequiredRequirement: boolean;
  isDisplayTopGroupName: boolean;
  isDisplayGroupName: boolean;
  isDisplayCompleteReception: boolean;
  isPhoneBookEnabled: boolean;
}

export interface MenuWords {
  menuHeaderTitle: string;
  isRedMenuHeaderTitle: boolean;
  menuHeaderSubTitle: string;
  isRedMenuHeaderSubTitle: boolean;
  groupButtonTitle: string;
  userButtonTitle: string;
  qrCodeButtonTitle: string;
  receptionNumberButtonTitle: string;
  recruitButtonTitle: string;
  deliveryButtonTitle: string;
  taxiButtonTitle: string;
  conversationReceptionButtonTitle: string;
  secretaryButtonTitle: string;
  otherButtonTitle: string;
  faceRecognitionButtonTitle: string;
}

export interface DeliveryWords {
  firstDeliveryTitle: string;
  secondDeliveryTitle: string;
  thirdDeliveryTitle: string;
  fourthDeliveryTitle: string;
  fifthDeliveryTitle: string;
  sixthDeliveryTitle: string;
  otherDeliveryTitle: string;
}

export interface ScreenWords {
  companyListHeaderTitle: string;
  companyNameTitle: string;
  schoolNameTitle: string;
  nameTitle: string;
  requirementTitle: string;
  companyNamePlaceHolderMessage: string;
  namePlaceHolderMessage: string;
  requirementPlaceHolderMessage: string;
  groupHeaderTitle: string;
  userHeaderTitle: string;
  searchUserPlaceHolderMessage: string;
  callConnectingMessage: string;
  callingMessage: string;
  receptionCompleteMessage: string;
  notificationCompleteMessage: string;
  receptionFailedMessage: string;
  deliveryMapMessage: string;
  deliveryReceptionCompleteMessage: string;
}

export interface AppSettingPayload {
  groupDisplayId: number;
  appSetting: {
    common?: Omit<SettingCommonEntity, "id">;
    standby?: Omit<StandbyEntity, "id">;
    call?: Omit<CallInfoEntity, "id">;
    words?: Array<{
      languageCode: string;
      menuWords: MenuWords;
      deliveryWords: DeliveryWords;
      screenWords: ScreenWords;
    }>;
    menuButton?: Omit<SettingMenuButtonEntity, "id">;
    basic?: Omit<SettingBasicEntity, "id">;
  };
}

export interface AppSettingsResponse {
  data: {
    list: AppSettingPayload[];
  };
}

export type ChatRequestType = "success" | "failed" | "noRequest";

export interface ReceptionDisplayState {
  groupName?: string;
  userName?: string;
}

export type ChatCompleteNavigationState = ReceptionDisplayState;

export interface RingUpPhoneNavigationState extends ReceptionDisplayState {
  chatRequestType: ChatRequestType;
  callType?: string;
  extensionNumber?: string;
  faceTimeAddress?: string;
  appointmentId?: number;
  appointmentVisitorIds?: number[];
}

export interface ReceptionByAppointmentGroupName {
  languageCode: string;
  groupName: string;
}

export interface ReceptionByAppointmentName {
  languageCode: string;
  name: string;
}
export interface ReceptionByAppointmentData {
  notificationType: NotificationType;
  groupNames: ReceptionByAppointmentGroupName[];
  names?: ReceptionByAppointmentName[];
  callType: CallType | null;
  extensionNumber?: string | null;
  faceTimeAddress?: string | null;
  isChatSuccess?: boolean;
  appointmentId: number;
  appointmentVisitorIds: number[];
}
export interface ReceptionByAppointmentResponse {
  data: ReceptionByAppointmentData;
}
